import React, { useEffect, useState, useRef } from "react";
import companyData from "./tableData.json";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  fetchReservationsDailyManifestReport,
  initializeReservationsDailyManifestReportScreen,
  setDeleteConfirmationPopupVisibility,
  exportReservationsDailyManifestReport,
  setDailyManifestFilters,
  clearDailyManifestFilters,
  setDailyManifestTableColumnFilters,
  clearDailyManifestTableColumnFilters,
  saveDailyManifestTableState,
} from "../../../redux/actions/Reservations/ReservationsDailyManifestReport";
import { fetchLocationDD } from "../../../redux/actions/location";
import { fetchRateAdminNetworkDD } from "../../../redux/actions/RateAdmin/rateadminRates";
import ReservationsDailyManifestReport from "../../../component/Reservations/ReservationsDailyManifestReport";
import { generatePath } from "react-router-dom";
import { loadingShow } from "../../../services/PostAPI";
import { APP_URLS } from "../../../utils/constant";
import { ProgressBar } from "primereact/progressbar";
import { setIsSearch } from "../../../redux/actions/common/search";
import { FILTER } from "../../../services/common/urlService";
import { DATE_STRATEGY, formatDate } from "../../../utils/date";

function ReservationsDailyManifestReportScreen(props) {
  const {
    fetchReservationsDailyManifestReport,
    initReservationsDailyManifestReportScreen,
    showPageLevelLoader,
    ReservationsDailyManifestReportListview,
    paginationInfo,
    screenPermissions,
    exportReservationsDailyManifestReport,
    isReservationsDailyManifestReportExportError,
    isReservationsDailyManifestReportExportSuccess,
    isLoading,
    fetchLocationDD,
    fetchRateAdminNetworkDD,
    RateNetworkDD,
    locationDD,
    isSearch,
    setIsSearch,
    dailyManifestFilters,
    setDailyManifestFilters,
    clearDailyManifestFilters,
    tableColumnFilters,
    setDailyManifestTableColumnFilters,
    clearDailyManifestTableColumnFilters,
    savedTableState,
    saveDailyManifestTableState,
  } = props;
  const navigate = useNavigate();
  const location = useLocation();
  
  // Track if we've already handled the initial load
  const hasInitialized = useRef(false);
  
  // Use ref to capture initial savedTableState to avoid closure issues
  const initialSavedState = useRef(savedTableState);

  const buildFetchParamsFromSavedState = (state) => {
    if (!state) return null;
    
    // Helper to check if a string looks like an ISO date
    const isISODateString = (str) => {
      if (typeof str !== 'string') return false;
      return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str);
    };
    
    // Helper to format date values to YYYY-MM-DD
    const formatFilterValue = (value) => {
      if (value instanceof Date) {
        return formatDate(value, DATE_STRATEGY.DATE_ISO);
      }
      if (isISODateString(value)) {
        return formatDate(new Date(value), DATE_STRATEGY.DATE_ISO);
      }
      return value;
    };
    
    // Build filter params in the format expected by the API
    const filters = [];
    if (state.filters) {
      Object.keys(state.filters).forEach(key => {
        if (key !== 'global' && state.filters[key]?.value !== null && state.filters[key]?.value !== undefined) {
          const filterValue = state.filters[key].value;
          filters.push({
            filterField: state.filters[key].filterKey || key,
            matchStrategy: state.filters[key].matchStrategy || FILTER.MATCH_STRATEGY.CONTAINS,
            query: formatFilterValue(filterValue)
          });
        }
      });
    }
    
    return {
      sortField: state.sortField || 'id',
      sortOrder: state.sortOrder,
      pageNumber: state.page || 0,
      pageSize: state.rows || 10,
      filters: filters.length > 0 ? filters : undefined,
      userId: localStorage.getItem("userId"),
      additinalFilter: dailyManifestFilters,
    };
  };

  const callAPIasync = async () => {
    await fetchRateAdminNetworkDD();
    await fetchLocationDD();
  };

  useEffect(() => {
    callAPIasync();
    
    // Check if coming from detail page (back button) BEFORE calling init
    const isFromDetail = location.state?.fromDailyManifest;
    
    if (isFromDetail && !hasInitialized.current) {
      hasInitialized.current = true;
      // Coming back from detail page - DO NOT call init, preserve filters
      // Fetch with saved table state params if available, otherwise use defaults
      const savedParams = buildFetchParamsFromSavedState(initialSavedState.current);
      
      if (savedParams) {
        fetchReservationsDailyManifestReport(savedParams);
      } else {
        // Build column filters array from tableColumnFilters for API call
        const columnFilters = Object.entries(tableColumnFilters || {}).map(([key, value]) => ({
          filterField: key,
          matchStrategy: 'CONTAINS',
          query: value
        })).filter(f => f.query);
        
        // Use saved pagination from initialSavedState if available
        const savedState = initialSavedState.current;
        
        // Fetch with preserved filters and pagination
        fetchReservationsDailyManifestReport({
          pageNumber: savedState?.page || 0,
          pageSize: savedState?.rows || 10,
          sortField: savedState?.sortField || "id",
          sortOrder: savedState?.sortOrder,
          userId: localStorage.getItem("userId"),
          additinalFilter: dailyManifestFilters,
          filters: columnFilters,
        });
      }
      // Clear the navigation state
      navigate(location.pathname, { replace: true, state: {} });
    } else if (!hasInitialized.current) {
      hasInitialized.current = true;
      // Coming from any other menu - call init and clear all filters
      initReservationsDailyManifestReportScreen();
      clearDailyManifestFilters();
      clearDailyManifestTableColumnFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableStateChange = (tableState) => {
    saveDailyManifestTableState(tableState);
  };

  const loadLazyData = (props) => {
    let newProps = {
      ...props,
      userId: localStorage.getItem("userId"),
      // Use passed additinalFilter if provided, otherwise use dailyManifestFilters from state
      additinalFilter: props.additinalFilter ?? dailyManifestFilters,
    };
    fetchReservationsDailyManifestReport(newProps);
  };
  const handleCreateReservationsDailyManifestReport = () => {
    navigate(APP_URLS.RESERVATIONS_DAILYMANIFESTREPORT.CREATE);
  };
  const editReservationsDailyManifestReportRoute = (id) => {
    const path = generatePath(APP_URLS.RESERVATIONS_DAILYMANIFESTREPORT.EDIT, {
      id,
    });
    navigate(path);
  };
  const viewReservationRoute = (id) => {
    navigate(`/view-reservations/${id}/view`, { state: { fromDailyManifest: true } });
  };
  const ReservationsDailyManifestReportProps = {
    handleCreateReservationsDailyManifestReport,
    exportReservationsDailyManifestReport,
    isReservationsDailyManifestReportExportError,
    isReservationsDailyManifestReportExportSuccess,
    editReservationsDailyManifestReportRoute,
    viewReservationRoute,
    screenPermissions,
    setDeleteConfirmationPopupVisibility,
    fetchReservationsDailyManifestReports: loadLazyData,
    tableData: {
      columns: ReservationsDailyManifestReportListview?.columns,
      rows: ReservationsDailyManifestReportListview?.rows,
    },
    paginationInfo: {
      pageSize: paginationInfo?.pageSize,
      pageNumber: paginationInfo?.pageNumber,
      totalPages: paginationInfo?.totalPages,
      totalRecords: paginationInfo?.totalRecords,
    },
    isLoading,
    RateNetworkDD,
    locationDD,
    setIsSearch,
    dailyManifestFilters,
    setDailyManifestFilters,
    clearDailyManifestFilters,
    tableColumnFilters,
    setTableColumnFilters: setDailyManifestTableColumnFilters,
    clearTableColumnFilters: clearDailyManifestTableColumnFilters,
    savedTableState,
    onTableStateChange: handleTableStateChange,
  };

  const renderProgressBar = () => {
    return <ProgressBar mode="indeterminate" style={{ height: "6px" }} />;
  };

  if (showPageLevelLoader) {
    loadingShow("block");
    return null;
  }
  loadingShow("none");
  return (
    <div>
      {isLoading && renderProgressBar()}
      <ReservationsDailyManifestReport
        {...ReservationsDailyManifestReportProps}
      />
    </div>
  );
}

const mapDispatchToProps = (dispatch) => {
  return {
    setIsSearch: (payload) => dispatch(setIsSearch(payload)),
    initReservationsDailyManifestReportScreen: () =>
      dispatch(initializeReservationsDailyManifestReportScreen()),
    fetchReservationsDailyManifestReport: (payload) =>
      dispatch(fetchReservationsDailyManifestReport(payload)),
    setDeleteConfirmationPopupVisibility: (payload) =>
      dispatch(setDeleteConfirmationPopupVisibility(payload)),
    fetchRateAdminNetworkDD: (payload) =>
      dispatch(fetchRateAdminNetworkDD(payload)),
    fetchLocationDD: (payload) => dispatch(fetchLocationDD(payload)),
    exportReservationsDailyManifestReport: (payload) =>
      dispatch(exportReservationsDailyManifestReport(payload)),
    setDailyManifestFilters: (filters) =>
      dispatch(setDailyManifestFilters(filters)),
    clearDailyManifestFilters: () =>
      dispatch(clearDailyManifestFilters()),
    setDailyManifestTableColumnFilters: (filters) =>
      dispatch(setDailyManifestTableColumnFilters(filters)),
    clearDailyManifestTableColumnFilters: () =>
      dispatch(clearDailyManifestTableColumnFilters()),
    saveDailyManifestTableState: (payload) =>
      dispatch(saveDailyManifestTableState(payload)),
  };
};

//vehicleCagegoryReducer
const mapStateToProps = (state, ownProps) => {
  return {
    isLoading: selectFromReservationsDailyManifestReportReducer(
      state,
      "isLoading"
    ),
    isSearch: selectFromReservationsDailyManifestReportReducer(
      state,
      "isSearch"
    ),
    isReservationsDailyManifestReportCreateSuccess:
      selectFromReservationsDailyManifestReportReducer(
        state,
        "isReservationsDailyManifestReportCreateSuccess"
      ),
    ReservationsDailyManifestReportListview:
      selectFromReservationsDailyManifestReportReducer(
        state,
        "ReservationsDailyManifestReportListview"
      ),
    showPageLevelLoader: selectFromReservationsDailyManifestReportReducer(
      state,
      "showPageLevelLoader"
    ),
    paginationInfo: selectFromReservationsDailyManifestReportReducer(
      state,
      "paginationInfo"
    ),
    isPageLevelError: selectFromReservationsDailyManifestReportReducer(
      state,
      "isPageLevelError"
    ),
    isFetchingGridData: selectFromReservationsDailyManifestReportReducer(
      state,
      "isFetchingGridData"
    ),
    RateNetworkDD: selectFromRateAdminRatesReducer(state, "RateNetworkDD"),
    locationDD: selectFromLocationReducer(state, "locationDD"),
    isReservationsDailyManifestReportExportError:
      selectFromReservationsDailyManifestReportReducer(
        state,
        "isReservationsDailyManifestReportExportError"
      ),
    isReservationsDailyManifestReportExportSuccess:
      selectFromReservationsDailyManifestReportReducer(
        state,
        "isReservationsDailyManifestReportExportSuccess"
      ),
    dailyManifestFilters: selectFromReservationsDailyManifestReportReducer(
      state,
      "dailyManifestFilters"
    ),
    tableColumnFilters: selectFromReservationsDailyManifestReportReducer(
      state,
      "tableColumnFilters"
    ),
    savedTableState: selectFromReservationsDailyManifestReportReducer(
      state,
      "savedTableState"
    ),
    ...ownProps,
  };
};

const selectFromReservationsDailyManifestReportReducer = (state, path) => {
  return selectReservationsDailyManifestReportReducer(state)[path];
};

const selectReservationsDailyManifestReportReducer = ({
  ReservationsDailyManifestReportReducer,
}) => {
  return ReservationsDailyManifestReportReducer;
};

// For Network
const selectFromRateAdminRatesReducer = (state, path) => {
  return selectRateAdminRatesReducer(state)[path];
};

const selectRateAdminRatesReducer = ({ rateadminRatesReducer }) => {
  return rateadminRatesReducer;
};
//Location
const selectFromLocationReducer = (state, path) => {
  return selectLocationReducer(state)[path];
};
const selectLocationReducer = ({ locationReducer }) => {
  return locationReducer;
};
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ReservationsDailyManifestReportScreen);
